import '/assets/styles/partials/event-ticket-selector.scss';
import { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useTranslation } from '/utilities/i18n';

// Functions
import formatPrice from '/functions/format-price';
import uid from '@wearetla/tla-essentials-tools/functions/uid';

// Partials
import Btn from '/views/partials/btn';
import { Input } from '/views/partials/forms';
import TicketBlockSelector from "/views/partials/ticket-block-selector";

const emptyTicket = {
  option: null,
  slots: [],
}

export const EventTicketSelector = ({
    event,
    selectedTickets,
    onUpdateSelectedTickets: setSelectedTickets,
    className,
    initialOptionId
  }) => {
  const { t } = useTranslation();
  const allEventOptions = useMemo(() => event?.calendar?.options, [event]);
  const seatPlan = useMemo(() => event?.calendar?.venue_seat_plan?.plan_data, [event]);

  useEffect(() => {
    if(!selectedTickets?.length) {
      setSelectedTickets([
        {
          ...emptyTicket,
          uid: uid(),
          option: allEventOptions.find(option => option.id.toString() === initialOptionId),
        }
      ]);
    }
  }, [selectedTickets, initialOptionId])

  const addTicket = () => {
    setSelectedTickets((prevSelectedTickets) => [
      ...prevSelectedTickets,
      {
        uid: uid(),
        emptyTicket,
      }
    ])
  }

  const updateTicket = (ticketIndex, ticketData) => {
    setSelectedTickets((prevSelectedTickets) => prevSelectedTickets.reduce((newSelectedTickets, ticket, nth) => {
      if(nth === ticketIndex) {
        return [
          ...newSelectedTickets,
          ticketData
        ]
      }
      return [
        ...newSelectedTickets,
        ticket,
      ]
    }, []))
  }

  const deleteTicket = (ticketIndex) => {
    setSelectedTickets((prevSelectedTickets) => prevSelectedTickets.filter((ticket, nth) => nth !== ticketIndex));
  }

  if(!allEventOptions?.length) {
    return null;
  }

  return (
    <div className={classNames('eventticketselector', className)}>
      {selectedTickets.map((ticket, nth) => (
        <OptionInput
          key={ticket.uid}
          selectedTickets={selectedTickets}
          ticket={ticket}
          event={event}
          onUpdate={(ticketData) => { updateTicket(nth, ticketData) }}
          onDelete={() => { deleteTicket(nth); }}
          allEventOptions={allEventOptions}
          seatPlan={seatPlan} />
      ))}
      
      {(selectedTickets.length < allEventOptions.length) &&
        <Btn
          className="eventticketselector-addbtn primary"
          icon="plus"
          onClick={() => { addTicket(); }}>
          {t('Başka Bilet Seçeneği Ekle')}
        </Btn>
      }
    </div>
  )
}

const OptionInput = ({
  allEventOptions,
  ticket,
  onUpdate,
  onDelete,
  seatPlan,
  selectedTickets
}) => {
  const { t } = useTranslation();
  const availableOptions = useMemo(() => {
    return allEventOptions.filter(option => (
      option.id === ticket.option?.id || !selectedTickets.find(ticket => ticket.option?.id === option.id)
    )).map((option) => ({
      label: `${option.title} (${formatPrice(option.price)})`,
      value: option.id,
      rawOption: option,
    }))
  }, [allEventOptions, ticket, selectedTickets]);

  const [selectedOption, setSelectedOption] = useState(availableOptions.find(availableOption => availableOption.rawOption?.id === ticket.option?.id));

  const isNumberedTicket = useMemo(() => !(ticket.option?.slots?.length === 1 && ticket.option.slots[0].is_multiple_buy), [ticket]);

  const maxQuantity = ticket.option?.stock ?? 10;

  useEffect(() => {
    if(ticket.option?.id !== selectedOption?.rawOption?.id) {
      onUpdate({
        ...ticket,
        option: selectedOption?.rawOption,
        slots: [],
      })
    }
  }, [selectedOption, ticket])

  const increaseQuantity = () => {
    onUpdate({
      ...ticket,
      slots: [
        ...ticket.slots,
        {
          ...ticket.option.slots[0],
          label: t('Numarasız')
        }
      ]
    })
  }

  const decreaseQuantity = () => {
    onUpdate({
      ...ticket,
      slots: ticket.slots.filter((slot, nth) => nth < ticket.slots.length - 1),
    })
  }

  const updateSelectionSlots = (newSlots) => {
    onUpdate({
      ...ticket,
      slots: newSlots,
    })
  }

  return (
    <div className="eventticketselector-ticket">
      <div className="ticket-topsection">
        <Input
          type="select"
          className="ticket-optioninput"
          label={t('Bilet Seçeneği Seçin')}
          options={availableOptions}
          onChange={setSelectedOption}
          value={selectedOption} />

          {(!ticket?.option || (ticket?.option && !isNumberedTicket)) &&
            <div className="controls-quantityinput">
              <Btn
                className="primary"
                icon="minus" 
                disabled={!ticket?.slots?.length || !ticket?.option}
                onClick={decreaseQuantity} />
              <span className="quantityinput-label">
                {ticket?.slots?.length ?? 0}
              </span>
              <Btn
                className="primary"
                disabled={(ticket?.slots?.length ?? 0) >= maxQuantity || !ticket?.option}
                icon="plus"
                onClick={increaseQuantity} />
            </div>
          }

          <Btn
            className="ticket-deletebtn text"
            disabled={selectedTickets.length < 2}
            onClick={onDelete}
            icon="trash"></Btn>
      </div>

      {(!!ticket?.option && isNumberedTicket) &&
        <div className="ticket-controls">
          <TicketBlockSelector
            className="controls-seatselector"
            seatPlan={seatPlan}
            availableSlots={ticket.option?.slots ?? []}
            selectedSlots={ticket.slots}
            lockedSlots={[]}
            selectionLimit={maxQuantity}
            onChangeSelection={updateSelectionSlots} />
        </div>
      }
    </div>
  )
}

export default EventTicketSelector;